import type { PlayerState } from '@/models/types';
import { supabase } from '@/config/supabase';
import { RaidManager } from '@/systems/RaidManager';

export class SaveManager {
  private playerId: string | null = null;
  private autoSaveInterval: ReturnType<typeof setInterval> | null = null;

  setPlayerId(id: string): void {
    this.playerId = id;
  }

  async load(): Promise<PlayerState | null> {
    if (!this.playerId) return null;
    const { data, error } = await supabase
      .from('players')
      .select('*')
      .eq('id', this.playerId)
      .single();
    if (error || !data) return null;
    return {
      id: data.id,
      username: data.username,
      era: data.era,
      prestigeCount: data.prestige_count,
      prestigeMultiplier: data.prestige_multiplier,
      gems: data.gems,
      resources: data.resources,
      buildings: data.buildings,
      productionRate: data.production_rate,
      lastTick: data.last_tick,
      streakCount: data.streak_count,
      lastLoginDate: data.last_login_date,
      achievementCounters: data.achievement_counters ?? {},
      unlockedAchievements: data.unlocked_achievements ?? [],
      arcaneShards: data.arcane_shards ?? 0,
      talentLevels: data.talent_levels ?? {},
      cosmeticsOwned: data.cosmetics_owned ?? [],
      cosmeticsEquipped: data.cosmetics_equipped ?? { frame: 'default', badge: 'none', name_color: '#c9a44c' },
      battlePass: data.battle_pass ?? { seasonId: 's1', xp: 0, tier: 0, claimed: [], claimedPremium: [], premium: false },
    };
  }

  async save(state: PlayerState): Promise<boolean> {
    if (!this.playerId) return false;
    const { error } = await supabase
      .from('players')
      .update({
        era: state.era,
        prestige_count: state.prestigeCount,
        prestige_multiplier: state.prestigeMultiplier,
        gems: state.gems,
        resources: state.resources,
        buildings: state.buildings,
        defense_power: RaidManager.calculateDefensePower(state.buildings),
        production_rate: state.productionRate,
        last_tick: Date.now(),
        streak_count: state.streakCount,
        last_login_date: state.lastLoginDate,
        achievement_counters: state.achievementCounters,
        unlocked_achievements: state.unlockedAchievements,
        arcane_shards: state.arcaneShards,
        talent_levels: state.talentLevels,
        cosmetics_owned: state.cosmeticsOwned,
        cosmetics_equipped: state.cosmeticsEquipped,
        battle_pass: state.battlePass,
      })
      .eq('id', this.playerId);
    return !error;
  }

  async createPlayer(id: string, username: string): Promise<boolean> {
    const { error } = await supabase
      .from('players')
      .insert({ id, username });
    return !error;
  }

  startAutoSave(getState: () => PlayerState, intervalMs: number = 30000): void {
    this.stopAutoSave();
    this.autoSaveInterval = setInterval(() => {
      this.save(getState());
    }, intervalMs);
  }

  stopAutoSave(): void {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
  }
}
